import { useEffect, useRef, useState } from 'react'
import { type OutcomeDraft } from './OutcomeSheet'
import { CLOSED_STAGES, OUTCOMES, requiresExplicitNextDate, type OutcomeKind } from '@/shared/domain'
import { buildLocalIso } from './leadUtils'
import { notifyDoNotContact } from './writeFeedback'
import { trackEvent } from '@/shared/api/telemetry'
import { clearsRating, nextCloseSource, triggerMeta, type KeyTrigger } from '@/shared/api/trackMeta'
import { useCanEdit } from '@/shared/write-access'
import { useLeadWrites } from './useLeadMutations'
import type { Lead, LeadRow } from '@/shared/types'
import type { Me } from '@/shared/api/session'
import type { EventInput } from './useLeadMutations'
import { defaultNextDate } from './businessDays'


interface OutcomeFlowArgs {
  selected: LeadRow | null
  me: Me | undefined
  overlayLeadId: string | null
  advance: () => void
}

interface OutcomeFlow {
  outcome: OutcomeDraft | null
  setOutcome: React.Dispatch<React.SetStateAction<OutcomeDraft | null>>
  startOutcome: (kind: OutcomeKind, comment?: string, via?: KeyTrigger) => void
  submitOutcome: (via?: KeyTrigger) => void
  /** Cancels the open sheet without saving — Escape or the Cancel/X control.
   *  Distinct from `setOutcome(null)` (still exposed, for field edits) so the
   *  abandon tracking below knows what triggered the close. */
  closeOutcome: (via?: KeyTrigger) => void
}

export function useOutcomeFlow({
  selected,
  me,
  overlayLeadId,
  advance,
}: OutcomeFlowArgs): OutcomeFlow {
  const canEdit = useCanEdit()
  const [outcome, setOutcome] = useState<OutcomeDraft | null>(null)
  const { saveOutcome } = useLeadWrites()

  // The sheet is dismissed from three places — Escape in App's key handler, the
  // sheet's own close control, and submitOutcome — so an abandoned outcome is
  // read off the state transition instead of being instrumented at each exit,
  // which is the only way Escape gets counted at all. `resolvedRef` marks the
  // closes that were not abandonment: a submit, and the read-only bail that
  // drops the write on purpose.
  const openRef = useRef<{ kind: OutcomeKind; leadId: string | null } | null>(null)
  const resolvedRef = useRef(false)
  // A double click on Save, or holding Enter, calls submitOutcome twice before
  // React re-renders and closes the sheet — both calls still see the same
  // non-null `outcome`, so that check alone doesn't catch it. This is a ref,
  // not state, so the second call sees the flip within the same tick rather
  // than on the next render. Cleared in startOutcome, when a new draft opens.
  const submittingRef = useRef(false)
  // What closeOutcome was last called with, read by the abandon effect below
  // once React gets around to the close it triggered. A plain setOutcome(null)
  // (still used for field edits via the functional form) never touches this,
  // so an abandon the effect catches some other way reports no trigger.
  const closeSourceRef = useRef<KeyTrigger | undefined>(undefined)
  useEffect(() => {
    if (outcome) {
      openRef.current = { kind: outcome.kind, leadId: selected?.id ?? null }
      resolvedRef.current = false
      return
    }
    const opened = openRef.current
    openRef.current = null
    const via = closeSourceRef.current
    closeSourceRef.current = undefined
    if (!opened || resolvedRef.current) return
    trackEvent('outcome_abandoned', { outcome: opened.kind, ...triggerMeta(via) }, opened.leadId ?? undefined)
  }, [outcome, selected?.id])

  // nextCloseSource only overwrites the ref when a via is actually given:
  // Escape tags via ModalShell's onEscapeKeyDown (composed by Radix ahead of
  // its own dismiss), and that dismiss then calls this same function again
  // with no via, through the plain onCancel every close path shares with the
  // Cancel button and the X. Resetting unconditionally would let that second,
  // source-less call erase the tag the first call just set.
  const closeOutcome = (via?: KeyTrigger) => {
    closeSourceRef.current = nextCloseSource(closeSourceRef.current, via)
    setOutcome(null)
  }

  // `comment` lets the activity composer hand over what the BDR already typed,
  // so writing the note and saying what it was is one action rather than two.
  const startOutcome = (kind: OutcomeKind, comment = '', via?: KeyTrigger) => {
    // The outcome buttons are disabled in read-only mode; this keeps the
    // R/N/B/X/C/W/E shortcuts from opening a sheet whose save would be dropped.
    if (!canEdit) return
    submittingRef.current = false
    trackEvent('outcome_started', { outcome: kind, ...triggerMeta(via) }, selected?.id)
    const nextDate = OUTCOMES[kind].askNext ? defaultNextDate(kind) : ''
    // Reached-only fields start from what the lead already carries, so logging
    // another Reached call doesn't silently wipe them.
    setOutcome({
      kind, rating: 0, comment, nextDate, nextTime: '', nextNote: '',
      moveDate: selected?.moveDate?.slice(0, 10) ?? '',
      officeHotel: !!selected?.officeHotel,
      likelyBooking: !!selected?.likelyBooking,
    })
  }

  const submitOutcome = (via?: KeyTrigger) => {
    if (!outcome || !selected) return
    // Marked Kontakta ej while the sheet was open: the server would refuse the
    // outcome, so it is not sent.
    if (selected.doNotContact) {
      resolvedRef.current = true
      setOutcome(null)
      notifyDoNotContact(selected.company)
      return
    }
    if (requiresExplicitNextDate(outcome.kind) && !outcome.nextDate) return
    if (submittingRef.current) return
    submittingRef.current = true
    // Read-only means the write below is dropped, so recording the outcome would
    // report something that never happened — an admin browsing a seller's queue
    // would inflate `outcome_logged` for that seller. Same class as the phantom
    // `search_run` on table mount: instrument the action, not the click.
    if (!canEdit) {
      resolvedRef.current = true
      setOutcome(null)
      return
    }
    // A cleared date on an outcome that still asks for one falls back to the
    // outcome's own horizon. `callback_set` stays tied to what the BDR chose,
    // so a fallback isn't reported as a callback they scheduled.
    const nextDate = OUTCOMES[outcome.kind].askNext
      ? outcome.nextDate || defaultNextDate(outcome.kind)
      : ''
    resolvedRef.current = true
    trackEvent('outcome_logged', { outcome: outcome.kind, ...triggerMeta(via) }, selected.id)
    if (outcome.nextDate) trackEvent('callback_set', undefined, selected.id)
    // Outcomes that don't ask for a rating still write 0 into the same feedback
    // blob, so submitting one over a rated lead throws the rating away without
    // saying so.
    if (clearsRating(selected.feedback?.rating, outcome.rating)) {
      trackEvent('rating_cleared', { outcome: outcome.kind }, selected.id)
    }
    const def = OUTCOMES[outcome.kind]
    const patch: Partial<Lead> = { ...def.patch }
    if (def.stage) patch.stage = def.stage
    patch.feedback = {
      rating: outcome.rating,
      comment: outcome.comment.trim(),
      outcome: outcome.kind,
      submittedAt: new Date().toISOString(),
      submittedBy: me?.name || me?.email || 'BDR',
    }
    // Reached-only fields. Sent on every Reached submit so clearing the date
    // (null) and unchecking a box persist. moveDate goes as a date-only string
    // the server folds into a timestamp. A lead that closes is no longer in the
    // pipe, so its likely-booking flag goes with it.
    if (outcome.kind === 'reached') {
      patch.moveDate = outcome.moveDate || null
      patch.officeHotel = outcome.officeHotel
      patch.likelyBooking = outcome.likelyBooking
    } else if (def.stage && (CLOSED_STAGES as readonly string[]).includes(def.stage) && selected.likelyBooking) {
      patch.likelyBooking = false
    }
    if (nextDate) {
      // When the BDR opted into a specific callback time, fold it into the
      // ISO string in their local timezone so the server stores the moment
      // they intended (10:00 in Stockholm, not naive). Without a time the
      // existing date-only behaviour stands — the lead surfaces silently
      // when the date passes.
      const callbackAt = outcome.nextTime
        ? buildLocalIso(nextDate, outcome.nextTime)
        : nextDate
      patch.nextContactAt = callbackAt
      patch.nextContactNote = outcome.nextNote.trim()
      // is_callback_alert tags this as a promised-time callback — drives
      // the in-app notification poller (RADAR-SE-022). Routine date-only
      // follow-ups clear the flag so an earlier alert doesn't linger.
      patch.isCallbackAlert = !!outcome.nextTime && outcome.kind !== 'booked'
      // The Booked outcome sheet labels its date input "Meeting date" — so
      // for booked outcomes, mirror the value into meeting_at too. Otherwise
      // the dashboard's show-rate calc (which keys off cl.meeting_at) never
      // gets a meeting date for bookings made via the focus view.
      if (outcome.kind === 'booked') {
        patch.meetingAt = callbackAt
      }
    } else {
      // Outcome didn't ask for a next contact — clear any prior alert flag.
      patch.isCallbackAlert = false
    }
    // Append a timeline event so the attempt survives (PATCH only writes
    // the latest feedback blob — every prior attempt would otherwise be
    // lost). For call outcomes, the server's append_event side-effects
    // bump stage New→Calling on a try and Calling→Contacted on a Reach,
    // and flip got_contact when applicable. For email_sent we log as
    // 'email' so the chat reads "Email sent" rather than "Call".
    //
    // The next-activity note is also folded into the body so a BDR who
    // wrote "send pricing proposal" as the follow-up reason doesn't have
    // to retype it in the timeline.
    const bodyParts: string[] = []
    const comment = outcome.comment.trim()
    const next = outcome.nextNote.trim()
    if (comment) bodyParts.push(comment)
    if (next) {
      bodyParts.push(nextDate ? `→ Nästa (${nextDate}): ${next}` : `→ Nästa: ${next}`)
    }
    const callEvent: EventInput = {
      kind: def.eventKind,
      outcome: outcome.kind,
      body: bodyParts.join('\n') || undefined,
    }
    // The event and its field patch commit as one request (YLLW-CRM-107), so
    // a dropped write can never log the call without its stage/follow-up
    // effect. When triggered from the overlay, just apply + close — no swipe
    // animation, no advance to next-in-queue; the overlay shows a single
    // lead, not a card stack.
    const targetId = selected.id
    setOutcome(null)
    saveOutcome(targetId, patch, callEvent)
    if (!overlayLeadId) advance()
  }

  return { outcome, setOutcome, startOutcome, submitOutcome, closeOutcome }
}
